import { Flex } from "@chakra-ui/react";
import { type JSX, useEffect, useState } from "react";
import { Collapse } from "../collapse/Collapse";
import { Icon } from "../icon/Icon";
import type { ContainerProps } from "./Container";
import { Container } from "./Container";

export interface BasicCardProps extends ContainerProps {
  header?: string | JSX.Element;
  canCollapse?: boolean;
  enableCollapseClick?: boolean;
  collapseIconLeft?: boolean;
  isOpen?: boolean;
  setIsOpen?: any;
  "data-cy"?: string;
  hasBackground?: boolean;
  headerPadding?: string;
  hasGap?: boolean;
  children:
    | JSX.Element
    | string
    | null
    | Array<JSX.Element | JSX.Element[] | string | null>;
}

/**
 * A surface that groups related content into a single container, helping
 * users scan, organize, and interact with information.
 */
export const BasicCard = ({
  header,
  canCollapse = false,
  enableCollapseClick = true,
  collapseIconLeft = false,
  isOpen = true,
  setIsOpen,
  "data-cy": dataCy,
  children,
  hasBackground = true,
  headerPadding = "0",
  hasGap = true,
  ...rest
}: BasicCardProps) => {
  const [isExpanded, setIsExpanded] = useState(isOpen);
  useEffect(() => {
    setIsExpanded(isOpen);
  }, [isOpen]);

  return (
    <Container
      display="flex"
      flexDirection="column"
      p={hasBackground ? "6" : "0"}
      minW="200px"
      gap={hasGap ? "6" : "0"}
      h="fit-content"
      data-cy={dataCy}
      {...(!hasBackground && { backgroundColor: "transparent" })}
      {...rest}
    >
      {(header || canCollapse) && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          p={headerPadding}
          onClick={() =>
            canCollapse && enableCollapseClick && setIsExpanded(!isExpanded)
          }
          cursor={canCollapse && enableCollapseClick ? "pointer" : "auto"}
        >
          {canCollapse && enableCollapseClick && collapseIconLeft && (
            <Icon
              mr="2"
              name={`actions/Chevron/${isExpanded ? "Up" : "Down"}`}
            />
          )}
          {header && (
            <Flex textStyle="subtitle4Bold" width="100%">
              {header}
            </Flex>
          )}
          {canCollapse && enableCollapseClick && !collapseIconLeft && (
            <Icon name={`actions/Chevron/${isExpanded ? "Up" : "Down"}`} />
          )}
        </Flex>
      )}

      {/*
        If the card isn't collapsible, don't render the Collapse
        component since it has a bit of extra height to it, even
        when closed.
      */}
      {canCollapse && (
        <Collapse in={!canCollapse || isExpanded}>{children}</Collapse>
      )}
      {!canCollapse && <>{children}</>}
    </Container>
  );
};
